import React from 'react';
import { AgGridReact } from '@ag-grid-community/react';
import { AllModules } from '@ag-grid-enterprise/all-modules';
import {
	ApButtonSecondary,
	ApButtonDanger,
	ApPopover,
	ApMenuItem,
} from '@alixpartners/ui-components';
import ClickOutside from 'react-click-outside';
import { MenuCellRenderer } from './MenuCellRenderer';
import {
	MANDATORY_COLUMNS,
	COLUMN_TYPES,
	COLUMNS,
	UNDO_ACTIONS,
	getColumnDefs,
	calculateBudgetData,
	getDefaultNewRow,
	getMonthCountFromData,
} from './utils';
import isEqual from 'lodash/isEqual';
import cloneDeep from 'lodash/cloneDeep';

import {
	TableWrapper,
	Wrapper,
	ContextualContent,
	Footer,
} from './BudgetTable.styles';
export class BudgetTable extends React.Component {
	state = {
		rowData: cloneDeep(this.props.data),
		contextualMenuData: null,
		monthCount: getMonthCountFromData(this.props.data),
	};
	gridApi = null;

	componentDidUpdate = prevProps => {
		const { undoHistory, data, isLoading } = this.props;
		const { undoHistory: prevUndoHistory } = prevProps;

		// Flash cells on undo edit action
		if (prevUndoHistory.length > undoHistory.length) {
			const lastActionData = prevUndoHistory.slice(-1)[0];

			if (lastActionData.action === UNDO_ACTIONS.EDIT) {
				this.setDataToRows(lastActionData.rows);
			}
		}

		if (!isEqual(data, prevProps.data)) {
			this.setState({ rowData: cloneDeep(this.props.data) });
			this.updateMonthCount();
		}

		if (isLoading !== prevProps.isLoading) {
			if (isLoading) {
				this.showLoadingOverlay();
			} else {
				this.hideLoadingOverlay();
			}
		}
	};

	updateMonthCount = () => {
		const { data } = this.props;

		this.setState({ monthCount: getMonthCountFromData(data) });
	};

	showLoadingOverlay = () => {
		if (!this.gridApi) {
			return;
		}

		// ag-grid known issue for older versions
		setTimeout(() => {
			this.gridApi.showLoadingOverlay();
		});
	};

	hideLoadingOverlay = () => {
		if (!this.gridApi) {
			return;
		}

		this.gridApi.hideOverlay();
	};

	onGridReady = params => {
		this.gridApi = params.api;
	};

	getGridOptions = () => {
		const { rowData, monthCount } = this.state;

		return {
			modules: AllModules,
			components: { menuCellRenderer: MenuCellRenderer },
			defaultColDef: {
				sortable: true,
				resizable: true,
				editable: true,
				filter: 'agTextColumnFilter',
			},
			floatingFilter: true,
			sideBar: {
				toolPanels: [
					{
						id: 'columns',
						labelDefault: 'Columns',
						labelKey: 'columns',
						iconKey: 'columns',
						toolPanel: 'agColumnsToolPanel',
						toolPanelParams: {
							suppressRowGroups: true,
							suppressValues: true,
							suppressPivots: true,
							suppressPivotMode: true,
						},
					},
					{
						id: 'filters',
						toolPanel: 'agFiltersToolPanel',
						labelDefault: 'Filters',
						labelKey: 'filters',
						iconKey: 'filter',
					},
				],
			},
			defaultToolPanel: '',
			rowData,
			columnDefs: getColumnDefs(monthCount),
			columnTypes: COLUMN_TYPES,
			rowSelection: 'multiple',

			onGridReady: this.onGridReady,
			getRowNodeId: data => data[COLUMNS.ID],
			onCellValueChanged: this.handleCellValueChanged,
			onCellFocused: this.handleCellFocused,
			onCellClicked: this.handleCellClicked,
		};
	};

	resetAllFilters = () => {
		if (this.gridApi) {
			this.gridApi.setFilterModel(null);
			this.gridApi.setSortModel(null);
		}
	};

	setFirstRowSelected = () => {
		if (this.gridApi) {
			const rowNode = this.gridApi.getDisplayedRowAtIndex(0);

			if (rowNode) {
				rowNode.setSelected(true, true);
			}
		}
	};

	handleCellClicked = e => {
		const { column } = e;

		if (column.colId !== 'contextualMenu') {
			return;
		}

		this.setState({ contextualMenuData: e });
	};

	hideContextualMenu = () => {
		this.setState({ contextualMenuData: null });
	};

	handleDelete = data => {
		const { onDelete } = this.props;

		onDelete(data[COLUMNS.ID]).then(() => {
			const { pushUndoAction } = this.props;

			pushUndoAction({ action: UNDO_ACTIONS.DELETE, rows: [data] });
		});
	};

	handleAddRows = duplicateData => {
		const { onAddNewRows } = this.props;
		const { monthCount } = this.state;
		const row = duplicateData ? duplicateData[0] : getDefaultNewRow(monthCount);
		const rowData = [{ ...row, [COLUMNS.CREATED_DATE]: undefined }];

		this.resetAllFilters();
		onAddNewRows(rowData).then(() => {
			const { data, pushUndoAction } = this.props;

			pushUndoAction({ action: UNDO_ACTIONS.CREATE, rows: [data[0]] });
			this.setFirstRowSelected();
		});
	};

	handleCellValueChanged = event => {
		const { monthCount } = this.state;
		const { oldValue, newValue, node } = event;
		const selectedRowNodes = this.gridApi.getSelectedNodes();

		if (
			oldValue &&
			newValue &&
			oldValue.toString() === newValue.toString() &&
			selectedRowNodes.length === 1
		) {
			return;
		}

		const { colId } = event.column;

		// Reset to previous value if mandatory column set empty
		if (MANDATORY_COLUMNS.includes(colId) && !newValue) {
			node.setData({
				...node.data,
				[colId]: oldValue,
			});

			return;
		}

		// Bulk edit
		let undoRows = [];
		const rowData = selectedRowNodes.map(rowNode => {
			undoRows = [
				...undoRows,
				{
					...rowNode.data,
					...(node.id === rowNode.id ? { [colId]: oldValue } : {}),
				},
			];
			// Calculate average or aggregated columns
			const updatedData = calculateBudgetData(
				colId,
				{
					...rowNode.data,
					[colId]: newValue,
				},
				monthCount
			);

			rowNode.setData(updatedData);

			return updatedData;
		});

		const { pushUndoAction, onEdit, onUndo } = this.props;

		pushUndoAction({ action: UNDO_ACTIONS.EDIT, rows: undoRows });

		onEdit(rowData).catch(() => {
			onUndo(true);
		});
	};

	setDataToRows = data => {
		if (this.gridApi) {
			data.forEach(rowData => {
				const rowNode = this.gridApi.getRowNode(rowData[COLUMNS.ID]);

				if (!rowNode) {
					return;
				}

				const flashColumns = [];

				Object.keys(rowNode.data).forEach(key => {
					if (rowNode.data[key] !== rowData[key]) {
						flashColumns.push(key);
					}
				});

				this.gridApi.flashCells({
					rowNodes: [rowNode],
					columns: flashColumns,
				});
				rowNode.setData(rowData);
			});
		}
	};

	handleCellFocused = e => {
		if (e && e.api) {
			const selectedRows = e.api.getSelectedNodes().map(i => i.rowIndex);
			if (selectedRows.includes(e.rowIndex)) {
				e.api.gridOptionsWrapper.gridOptions.suppressRowClickSelection = true;
			} else {
				e.api.gridOptionsWrapper.gridOptions.suppressRowClickSelection = false;
			}
		}
	};

	handleClickOutside = () => {
		if (this.gridApi) {
			this.gridApi.deselectAll();
		}
	};

	handleContextualDuplicate = () => {
		const { contextualMenuData } = this.state;

		this.handleAddRows([
			{
				...contextualMenuData.data,
				[COLUMNS.ID]: undefined,
			},
		]);
		this.hideContextualMenu();
	};

	handleContextualDelete = () => {
		const { contextualMenuData } = this.state;

		this.handleDelete(contextualMenuData.data);
		this.hideContextualMenu();
	};

	render() {
		const { onDeleteAll, data } = this.props;
		const { contextualMenuData } = this.state;

		return (
			<Wrapper>
				<ClickOutside
					onClickOutside={this.handleClickOutside}
					style={{ height: '100%' }}
				>
					<TableWrapper className="ag-theme-balham">
						<AgGridReact {...this.getGridOptions()} />
					</TableWrapper>
				</ClickOutside>

				{contextualMenuData && (
					<ApPopover
						referenceElement={
							contextualMenuData.event && contextualMenuData.event.target
						}
						onClose={this.hideContextualMenu}
						placement="bottom"
						isOpen={true}
						hideArrow
					>
						<ContextualContent>
							<ApMenuItem onClick={this.handleContextualDuplicate}>
								Duplicate
							</ApMenuItem>
							<ApMenuItem onClick={this.handleContextualDelete}>
								Delete
							</ApMenuItem>
						</ContextualContent>
					</ApPopover>
				)}

				<Footer>
					<ApButtonSecondary
						iconName="baseline_add"
						onClick={() => this.handleAddRows()}
					>
						Add new row
					</ApButtonSecondary>
					<ApButtonDanger
						iconName="delete_outline"
						onClick={onDeleteAll}
						disabled={data.length === 0}
					>
						Delete all data rows
					</ApButtonDanger>
				</Footer>
			</Wrapper>
		);
	}
}
